import { parsePaymentTerms } from "@/lib/backtest/paymentTermsParser";
import type { PARInvoiceRow, VendorSummary } from "@/lib/par/csvInvoiceParser";
import { suggestTerms } from "@/lib/negotiation/suggestTerms";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type SignalKey =
  | "mixed_terms"
  | "paid_late"
  | "high_spend"
  | "no_discount"
  | "paid_early_no_capture"
  | "long_terms";

export interface Signal {
  key: SignalKey;
  label: string;
}

export interface SuggestedVendor {
  vendor_name: string;
  score: number;
  signals: Signal[];
  /** All-time total invoiced (from VendorSummary). */
  total_invoiced: number;
  /**
   * Spend over the trailing 12-month window computed from available invoice dates.
   * Falls back to total_invoiced when no dated rows are available.
   */
  spend_12mo: number;
  /** Suggested opening discount % (target). */
  target_discount_pct: number;
  /** Minimum acceptable discount % (floor). */
  floor_discount_pct: number;
}

export interface NegotiationSignalConfig {
  // Thresholds
  high_spend_quantile: number;
  late_variance_stddev_days: number;
  long_terms_min_days: number;
  near_zero_capture: number;

  // Per-signal weights (keyed by signal key)
  weight_mixed_terms: number;
  weight_paid_late: number;
  weight_high_spend: number;
  weight_no_discount: number;
  weight_paid_early_no_capture: number;
  weight_long_terms: number;

  // Per-signal enabled toggles
  enabled_mixed_terms: boolean;
  enabled_paid_late: boolean;
  enabled_high_spend: boolean;
  enabled_no_discount: boolean;
  enabled_paid_early_no_capture: boolean;
  enabled_long_terms: boolean;
}

export const DEFAULT_NEGOTIATION_CONFIG: NegotiationSignalConfig = {
  high_spend_quantile: 0.75,
  late_variance_stddev_days: 15,
  long_terms_min_days: 45,
  near_zero_capture: 0.01,

  weight_mixed_terms: 1,
  weight_paid_late: 1,
  weight_high_spend: 1,
  weight_no_discount: 1,
  weight_paid_early_no_capture: 1,
  weight_long_terms: 1,

  enabled_mixed_terms: true,
  enabled_paid_late: true,
  enabled_high_spend: true,
  enabled_no_discount: true,
  enabled_paid_early_no_capture: true,
  enabled_long_terms: true,
};

export interface SuggestionsOpts {
  excludeVendorNames: Set<string>;
  config?: NegotiationSignalConfig;
  /**
   * The buyer's cost of capital (e.g. 12 for 12 %) used to compute suggested
   * target/floor discount terms.  Defaults to 12 when omitted.
   */
  costOfCapitalApr?: number;
}

// ---------------------------------------------------------------------------
// Signal definitions
// ---------------------------------------------------------------------------

const SIGNAL_LABELS: Record<SignalKey, string> = {
  mixed_terms: "Mixed payment terms",
  paid_late: "Paid late or sporadic",
  high_spend: "High spend (top quartile)",
  no_discount: "No early-pay discount available",
  paid_early_no_capture: "Paid early but no discount captured",
  long_terms: "Long net terms (≥45 days)",
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Compute invoiced spend for a vendor over a trailing 12-month window.
 *
 * Uses `invoiced_date` from invoice rows. Rows with unparseable dates are
 * silently skipped. If no rows have a parseable date within the window the
 * function returns null (the caller should fall back to total_invoiced).
 *
 * @param vendorRows  Invoice rows for this vendor only.
 * @param windowMs    Window size in ms (default: 12 months = 365 days).
 * @param asOf        Reference timestamp (default: Date.now()).
 */
function computeSpend12mo(
  vendorRows: PARInvoiceRow[],
  windowMs: number = 365 * 24 * 60 * 60 * 1000,
  asOf: number = Date.now()
): number | null {
  const cutoff = asOf - windowMs;
  let total = 0;
  let hasAnyDate = false;

  for (const row of vendorRows) {
    if (!row.invoiced_date) continue;
    const ts = new Date(row.invoiced_date).getTime();
    if (isNaN(ts)) continue;
    hasAnyDate = true;
    if (ts >= cutoff) {
      total += row.amount_invoiced || 0;
    }
  }

  return hasAnyDate ? total : null;
}

/** Variance of an array of numbers (population variance). */
function variance(values: number[]): number {
  if (values.length === 0) return 0;
  const mean = values.reduce((s, v) => s + v, 0) / values.length;
  return values.reduce((s, v) => s + (v - mean) ** 2, 0) / values.length;
}

/**
 * Parse every entry in terms_used and return the max net days found.
 * Returns null when no term string yields a parseable net-days value.
 */
function maxNetDays(termsUsed: string[]): number | null {
  let max: number | null = null;
  for (const t of termsUsed) {
    const parsed = parsePaymentTerms(t);
    if (parsed && parsed.netDays != null) {
      max = max == null ? parsed.netDays : Math.max(max, parsed.netDays);
    }
    // Also handle plain "Net N" / "NET N" strings without discount prefix
    const netOnly = t.match(/\bNET\s*(\d+)/i);
    if (netOnly) {
      const n = parseInt(netOnly[1], 10);
      max = max == null ? n : Math.max(max, n);
    }
  }
  return max;
}

// ---------------------------------------------------------------------------
// Per-vendor signal computation (no exclusion filtering)
// ---------------------------------------------------------------------------

export interface VendorSignalsAndScore {
  signals: Signal[];
  score: number;
}

/**
 * Compute signals and score for a single vendor WITHOUT applying any exclusion
 * filtering. Useful for the unified Vendors table which shows ALL vendors.
 *
 * The caller must pass the pre-computed `topQuartileThreshold` so this helper
 * doesn't need to re-derive it over the full vendor list.
 */
export function computeVendorSignalsAndScore(
  vendor: VendorSummary,
  vendorRows: PARInvoiceRow[],
  topQuartileThreshold: number,
  cfg: NegotiationSignalConfig = DEFAULT_NEGOTIATION_CONFIG
): VendorSignalsAndScore {
  const signals: Signal[] = [];

  // 1. Mixed terms
  if (cfg.enabled_mixed_terms && vendor.terms_used.length > 1) {
    signals.push({ key: "mixed_terms", label: SIGNAL_LABELS.mixed_terms });
  }

  // 2. Paid late or sporadic
  if (cfg.enabled_paid_late) {
    const dayValues = vendorRows.map((r) => r.days_paid_early).filter((d) => !isNaN(d));
    const daysVariance = variance(dayValues);
    const varianceThreshold = cfg.late_variance_stddev_days * cfg.late_variance_stddev_days;
    if (vendor.avg_days_paid_early < 0 || daysVariance > varianceThreshold) {
      signals.push({ key: "paid_late", label: SIGNAL_LABELS.paid_late });
    }
  }

  // 3. High spend (top quartile)
  if (
    cfg.enabled_high_spend &&
    vendor.total_invoiced >= topQuartileThreshold &&
    vendor.total_invoiced > 0
  ) {
    signals.push({ key: "high_spend", label: SIGNAL_LABELS.high_spend });
  }

  // 4. No early-pay discount available
  if (
    cfg.enabled_no_discount &&
    (vendor.best_discount_pct == null || vendor.best_discount_pct === 0)
  ) {
    signals.push({ key: "no_discount", label: SIGNAL_LABELS.no_discount });
  }

  // 5. Paid early but no discount captured
  if (
    cfg.enabled_paid_early_no_capture &&
    vendor.avg_days_paid_early > 0 &&
    vendor.total_discount <= cfg.near_zero_capture
  ) {
    signals.push({
      key: "paid_early_no_capture",
      label: SIGNAL_LABELS.paid_early_no_capture,
    });
  }

  // 6. Long terms
  if (cfg.enabled_long_terms) {
    const maxNet = maxNetDays(vendor.terms_used);
    if (maxNet != null && maxNet >= cfg.long_terms_min_days) {
      signals.push({ key: "long_terms", label: SIGNAL_LABELS.long_terms });
    }
  }

  const weightMap: Record<SignalKey, number> = {
    mixed_terms: cfg.weight_mixed_terms,
    paid_late: cfg.weight_paid_late,
    high_spend: cfg.weight_high_spend,
    no_discount: cfg.weight_no_discount,
    paid_early_no_capture: cfg.weight_paid_early_no_capture,
    long_terms: cfg.weight_long_terms,
  };

  const score = signals.reduce((s, sig) => s + weightMap[sig.key], 0);
  return { signals, score };
}

/**
 * Compute the top-quartile spend threshold for a list of vendors.
 * Exported so callers (e.g. buildVendorRoster) can share the threshold
 * when calling computeVendorSignalsAndScore per-vendor.
 */
export function computeTopQuartileThreshold(
  vendors: VendorSummary[],
  quantile: number = DEFAULT_NEGOTIATION_CONFIG.high_spend_quantile
): number {
  if (vendors.length === 0) return 0;
  const spends = vendors.map((v) => v.total_invoiced).sort((a, b) => a - b);
  const idx = Math.floor(spends.length * quantile);
  return spends[idx] ?? 0;
}

// ---------------------------------------------------------------------------
// Core function
// ---------------------------------------------------------------------------

/**
 * Build a ranked list of vendors that are good candidates for early-payment
 * discount negotiations.
 *
 * Vendors whose names appear in `opts.excludeVendorNames` are omitted entirely
 * (the caller should pass the union of: active/needs_attention thread vendor
 * names, agreed vendor_terms supplier names, and excluded vendor_contacts names).
 *
 * Pass `opts.config` to override thresholds, weights, and enabled toggles.
 * Omitting `opts.config` falls back to DEFAULT_NEGOTIATION_CONFIG (existing behaviour).
 */
export function buildNegotiationSuggestions(
  vendors: VendorSummary[],
  rows: PARInvoiceRow[],
  opts: SuggestionsOpts
): SuggestedVendor[] {
  if (vendors.length === 0) return [];

  const cfg = opts.config ?? DEFAULT_NEGOTIATION_CONFIG;
  const costOfCapitalApr = opts.costOfCapitalApr ?? 12;

  // Pre-compute top-quartile spend threshold using configured quantile
  const spends = vendors.map((v) => v.total_invoiced).sort((a, b) => a - b);
  const q3Index = Math.floor(spends.length * cfg.high_spend_quantile);
  const topQuartileThreshold = spends[q3Index] ?? 0;

  // Group rows by vendor for variance calculation
  const rowsByVendor = new Map<string, PARInvoiceRow[]>();
  for (const row of rows) {
    const key = row.vendor_name;
    if (!rowsByVendor.has(key)) rowsByVendor.set(key, []);
    rowsByVendor.get(key)!.push(row);
  }

  const results: SuggestedVendor[] = [];

  for (const vendor of vendors) {
    if (opts.excludeVendorNames.has(vendor.vendor_name)) continue;

    const signals: Signal[] = [];

    // 1. Mixed terms
    if (cfg.enabled_mixed_terms && vendor.terms_used.length > 1) {
      signals.push({ key: "mixed_terms", label: SIGNAL_LABELS.mixed_terms });
    }

    // 2. Paid late or sporadic (avg < 0 OR high variance in days_paid_early)
    if (cfg.enabled_paid_late) {
      const vendorRows = rowsByVendor.get(vendor.vendor_name) ?? [];
      const dayValues = vendorRows.map((r) => r.days_paid_early).filter((d) => !isNaN(d));
      const daysVariance = variance(dayValues);
      const varianceThreshold = cfg.late_variance_stddev_days * cfg.late_variance_stddev_days;
      if (vendor.avg_days_paid_early < 0 || daysVariance > varianceThreshold) {
        signals.push({ key: "paid_late", label: SIGNAL_LABELS.paid_late });
      }
    }

    // 3. High spend (top quartile)
    if (
      cfg.enabled_high_spend &&
      vendor.total_invoiced >= topQuartileThreshold &&
      vendor.total_invoiced > 0
    ) {
      signals.push({ key: "high_spend", label: SIGNAL_LABELS.high_spend });
    }

    // 4. No early-pay discount available
    if (
      cfg.enabled_no_discount &&
      (vendor.best_discount_pct == null || vendor.best_discount_pct === 0)
    ) {
      signals.push({ key: "no_discount", label: SIGNAL_LABELS.no_discount });
    }

    // 5. Paid early but no discount captured
    if (
      cfg.enabled_paid_early_no_capture &&
      vendor.avg_days_paid_early > 0 &&
      vendor.total_discount <= cfg.near_zero_capture
    ) {
      signals.push({
        key: "paid_early_no_capture",
        label: SIGNAL_LABELS.paid_early_no_capture,
      });
    }

    // 6. Long terms (Net >= configured threshold)
    if (cfg.enabled_long_terms) {
      const maxNet = maxNetDays(vendor.terms_used);
      if (maxNet != null && maxNet >= cfg.long_terms_min_days) {
        signals.push({ key: "long_terms", label: SIGNAL_LABELS.long_terms });
      }
    }

    if (signals.length === 0) continue;

    const weightMap: Record<SignalKey, number> = {
      mixed_terms: cfg.weight_mixed_terms,
      paid_late: cfg.weight_paid_late,
      high_spend: cfg.weight_high_spend,
      no_discount: cfg.weight_no_discount,
      paid_early_no_capture: cfg.weight_paid_early_no_capture,
      long_terms: cfg.weight_long_terms,
    };

    const score = signals.reduce((s, sig) => s + weightMap[sig.key], 0);

    // Windowed spend (trailing 12 months)
    const vendorRowsForSpend = rowsByVendor.get(vendor.vendor_name) ?? [];
    const windowedSpend = computeSpend12mo(vendorRowsForSpend);
    const spend_12mo = windowedSpend ?? vendor.total_invoiced;

    // Suggested terms for the subtext
    const terms = suggestTerms(vendor, rows, costOfCapitalApr);

    results.push({
      vendor_name: vendor.vendor_name,
      score,
      signals,
      total_invoiced: vendor.total_invoiced,
      spend_12mo,
      target_discount_pct: terms.target_discount_pct,
      floor_discount_pct: terms.floor_discount_pct,
    });
  }

  // Rank: higher score first; break ties by higher spend
  results.sort((a, b) => b.score - a.score || b.total_invoiced - a.total_invoiced);

  return results;
}
